import { useMemo } from 'react';

import { GridColDef, GridRowId } from '@mui/x-data-grid-pro';
import { useFormikContext } from 'formik';

import { MetaAutoComplete, TipoLogisticaLojaAutoComplete } from 'components/autocompletes';
import { PanelGrid } from 'components/grid';
import { ConfigPedidoRemoveBtn } from 'pages/compra/configuracao-pedido/components/form/ConfigPedidoRemoveBtn';
import { useConfigPedidoListContext } from 'pages/compra/configuracao-pedido/contexts';
import {
    ConfigPedidoPedidosSazonais,
    LojaMetaAtingidaProps,
    LojasProps,
    MetaProps,
    TipoLogisticaLojaProps,
} from 'pages/compra/configuracao-pedido/types';

/**
 * Retorna objeto de acordo com o valor de fgMetaAtingida.
 */
function getMetaAtingida(fgMetaAtingida: boolean) {
    return fgMetaAtingida ? { idMeta: 1, vlMeta: true, dsMeta: 'Sim' } : { idMeta: 2, vlMeta: false, dsMeta: 'Não' };
}

/**
 * Retorna as linhas da grid.
 */
const getRows = (lojas: LojaMetaAtingidaProps[] = []) =>
    lojas.map((item, index) => ({
        id: item.loja.idLoja,
        index,
        idLoja: item.loja.idLoja,
        cdLoja: item.loja.cdLoja,
        dsNomeFantasia: item.loja.dsNomeFantasia,
        fgMetaAtingida: item.fgMetaAtingida,
        metaAtingida: getMetaAtingida(item.fgMetaAtingida),
        tipoLogisticaLoja: item.tipoLogisticaLoja,
    }));

type GridRows = ReturnType<typeof getRows>;

/**
 * Filtra os produtos com base no valor do filtro.
 */
const getFilteredRows = (
    lojas: GridRows,
    valueFilterMetas: MetaProps | null,
    valueFilterLojas: LojasProps[],
    valueFilterLogisticaLoja: TipoLogisticaLojaProps[],
) => {
    const listIdLojasFilter = valueFilterLojas.map((loja) => loja.idLoja);
    const listIdLogisticaLojasFilter = valueFilterLogisticaLoja.map((l) => l.idTipoLogisticaLoja);

    return lojas.filter((metaLoja) => {
        const hasLogisticaLoja =
            !listIdLogisticaLojasFilter.length ||
            (metaLoja.tipoLogisticaLoja && listIdLogisticaLojasFilter.includes(metaLoja.tipoLogisticaLoja.idTipoLogisticaLoja));
        const hasLoja = !listIdLojasFilter.length || listIdLojasFilter.includes(metaLoja.idLoja);
        const hasMeta = !valueFilterMetas || valueFilterMetas.vlMeta === metaLoja.fgMetaAtingida;

        return hasLoja && hasMeta && hasLogisticaLoja;
    });
};

export function ConfigPedidoLojasMetasGrid() {
    const { setSelectedRows, onRemove, valueFilterLojas, valueFilterMetas, valueFilterLogisticaLoja } = useConfigPedidoListContext();
    const { values, setFieldValue } = useFormikContext<ConfigPedidoPedidosSazonais>();

    const rows = useMemo(() => {
        const rows = getRows(values.lojasMetaAtingida);

        return getFilteredRows(rows, valueFilterMetas, valueFilterLojas, valueFilterLogisticaLoja);
    }, [values.lojasMetaAtingida, valueFilterMetas, valueFilterLojas, valueFilterLogisticaLoja]);

    const columns: GridColDef[] = [
        { field: 'cdLoja', headerName: 'Código', type: 'number', minWidth: 80, align: 'center', headerAlign: 'center' },
        { field: 'dsNomeFantasia', headerName: 'Nome da Loja', type: 'string', minWidth: 100, flex: 1 },
        {
            field: 'fgMetaAtingida',
            headerName: 'Meta',
            type: 'string',
            minWidth: 110,
            flex: 1,
            renderCell: ({ row }) => (
                <MetaAutoComplete
                    sx={{ flex: 1 }}
                    label=""
                    size="small"
                    value={row.metaAtingida}
                    onChange={(_e, newMeta) => setFieldValue(`lojasMetaAtingida.${row.index}.fgMetaAtingida`, newMeta.vlMeta)}
                />
            ),
        },
        {
            field: 'tipoLogisticaLoja',
            headerName: 'Tipo de Loja',
            type: 'string',
            minWidth: 110,
            flex: 1,
            renderCell: ({ row }) => (
                <TipoLogisticaLojaAutoComplete
                    label=""
                    sx={{ flex: 1 }}
                    size="small"
                    value={row.tipoLogisticaLoja}
                    onChange={(_e, newTipoLogisticaLoja) => setFieldValue(`lojasMetaAtingida.${row.index}.tipoLogisticaLoja`, newTipoLogisticaLoja)}
                />
            ),
        },
        {
            field: 'remover',
            align: 'center',
            headerAlign: 'center',
            headerName: 'Remover',
            filterable: false,
            sortable: false,
            hideable: false,
            disableColumnMenu: true,
            width: 110,
            renderCell: ({ row }) => <ConfigPedidoRemoveBtn onClick={() => onRemove(new Set([row.idLoja]))} />,
        },
    ];

    return <PanelGrid rows={rows} reload={false} loading={false} columns={columns} remote={false} checkboxSelection onClickRow={setSelectedRows} />;
}
